// Shared Invite Member modal — extracted from SettingsAccessPage during
// task 104e D1 so the Employees page can trigger the same flow with a
// pre-filled, locked email and a pre-committed employeeId.
//
// Two modes:
//   - Access page (default): free-form email input; if the email matches
//     exactly one unlinked active Employee, show the "link on acceptance"
//     callout and a checkbox (matches the 104d behaviour).
//   - Employee page (lockedEmployee passed): email is pre-filled and the
//     field is rendered read-only + locked; the modal shows a chip "Will
//     link to employee <name>"; employeeId is pre-committed into the
//     POST body on submit.
//
// The modal calls `useInvite()` from `@/hooks/useOrgAccess` (the 104d
// mutation hook). Role picker + canInviteOwner behaviour is unchanged
// from the pre-extraction version.

import { useState, type FormEvent } from 'react';
import { SlidePanel } from '@/components/composites/SlidePanel';
import { Button } from '@/components/primitives/Button';
import { TextInput } from '@/components/primitives/TextInput';
import { Select } from '@/components/primitives/Select';
import { useInvite, type KanjoyUserRole } from '@/hooks/useOrgAccess';
import { useEmployeeSearch } from '@/hooks/useEmployeeSearch';
import styles from './InviteMemberModal.module.css';

export interface InviteMemberModalProps {
  open: boolean;
  onClose: () => void;
  /** When true, Role options include `owner`. Matches the 104d admin gate. */
  canInviteOwner: boolean;
  /**
   * Optional pre-fill for the Employees-page flow (104e D1). When supplied:
   *   - email is locked (read-only) to `lockedEmployee.email`
   *   - a chip "Will link to employee {name}" is rendered
   *   - the POST body carries `employeeId: lockedEmployee.id`
   *   - the email-match callout is suppressed (the link is already chosen)
   */
  lockedEmployee?: {
    id: string;
    name: string;
    email: string;
  };
}

export function InviteMemberModal({
  open,
  onClose,
  canInviteOwner,
  lockedEmployee,
}: InviteMemberModalProps) {
  const isLocked = lockedEmployee !== undefined;

  const [email, setEmail] = useState<string>(lockedEmployee?.email ?? '');
  const [role, setRole] = useState<KanjoyUserRole>('member');
  const [linkOnAccept, setLinkOnAccept] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const invite = useInvite();

  // Keep `email` in sync when the modal is re-opened for a different
  // employee — otherwise the first-mount value sticks across rows.
  if (isLocked && email !== lockedEmployee.email) {
    setEmail(lockedEmployee.email);
  }

  const { matches } = useEmployeeSearch(isLocked ? '' : email);
  const unlinkedMatches = matches.filter((m) => m.userId === null);
  const singleMatch = !isLocked && unlinkedMatches.length === 1 ? unlinkedMatches[0] : null;

  const reset = () => {
    setEmail(lockedEmployee?.email ?? '');
    setRole('member');
    setLinkOnAccept(true);
    setSubmitted(false);
  };

  const roleOptions = canInviteOwner
    ? [
        { value: 'member' as const, label: 'Member' },
        { value: 'admin' as const, label: 'Admin' },
        { value: 'owner' as const, label: 'Owner' },
      ]
    : [
        { value: 'member' as const, label: 'Member' },
        { value: 'admin' as const, label: 'Admin' },
      ];

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const body: {
      email: string;
      role: KanjoyUserRole;
      employeeId?: string;
    } = { email: email.trim(), role };
    if (isLocked) {
      body.employeeId = lockedEmployee.id;
    } else if (singleMatch && linkOnAccept) {
      body.employeeId = singleMatch.id;
    }
    invite.mutate(body, {
      onSuccess: () => {
        setSubmitted(true);
        setTimeout(() => {
          reset();
          onClose();
        }, 800);
      },
    });
  };

  return (
    <SlidePanel
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Invite Member"
      subtitle={
        isLocked
          ? `Send a Kanjoy invitation to ${lockedEmployee.name}.`
          : 'Send an invite to give someone access to this organization.'
      }
    >
      <form onSubmit={handleSubmit} className={styles.form}>
        <TextInput
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@firm.com"
          required
          readOnly={isLocked}
          aria-readonly={isLocked || undefined}
        />
        <Select<KanjoyUserRole>
          label="Role"
          value={role}
          options={roleOptions}
          onChange={(v) => setRole((v || 'member') as KanjoyUserRole)}
        />
        {isLocked && (
          <div
            className={styles.employeeChip}
            role="status"
            aria-live="polite"
            data-testid="employee-link-chip"
          >
            <span className={styles.chipLabel}>Will link to employee</span>
            <span className={styles.chipValue}>{lockedEmployee.name}</span>
          </div>
        )}
        {singleMatch && (
          <div
            className={styles.matchCallout}
            role="status"
            aria-live="polite"
            data-testid="employee-match-callout"
          >
            <p className={styles.matchText}>
              This email matches employee <strong>{singleMatch.name}</strong> — link on acceptance?
            </p>
            <label className={styles.matchCheckbox}>
              <input
                type="checkbox"
                checked={linkOnAccept}
                onChange={(e) => setLinkOnAccept(e.target.checked)}
              />
              <span>Link this invite to {singleMatch.name} when they accept</span>
            </label>
          </div>
        )}
        {submitted && (
          <p className={styles.success} role="status">
            Invite queued.
          </p>
        )}
        {invite.error ? (
          <p className={styles.error} role="alert">
            {(invite.error as Error).message}
          </p>
        ) : null}
        <div className={styles.formActions}>
          <Button
            variant="secondary"
            type="button"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={email.trim() === '' || submitted}>
            {submitted ? 'Sent' : 'Send invite'}
          </Button>
        </div>
      </form>
    </SlidePanel>
  );
}
